import React, { useState, useCallback, Suspense, lazy, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux';
import styles from './Explore.module.scss'
import CreationSkeleton from '../../components/CreationCard/CreationSkeleton';
import '../../components/Explore/FeedTab.scss';
import { useScrollPosition } from '../../../hooks/useScrollPosition';
import { setCurrentTab } from '../../Reducers/actions/navigationActions';
import ModalMessage from '../../components/ModalMessage/ModalMessage';
import CreationAdded from '../../components/CreationAdded/CreationAdded';
import { cleanError } from '../../Reducers/actions/uploadActions';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ComingSoon from '../../components/Heatmap/ComingSoon';
import { useSoftwall } from '../SoftWall/SoftWall';
import { hasSocialLinks } from '../../hooks/useSocialNetworksPrompt';
import {
  VERIFY_EMAIL_BANNER_EMAIL_KEY,
  requiresPasswordSetupFromSession,
  userNeedsEmailVerificationOrPasswordSetup,
} from '../../constants/signupFlow';
import { USER_ROLES } from '../../constants/globals';

// Lazy load heavy components
const FeedTab = lazy(() => import('../../components/Explore/FeedTab'));
const getProfileBannerDismissKey = (userId) =>
  `profile-complete-banner-dismissed:${userId || 'anonymous'}`;

function Explore() {
  const dispatch = useDispatch();
  const navigate = useNavigate()
  const [searchParams] = useSearchParams();
  const sw = useSoftwall();
  const _activeTab = useSelector(state => state.navigation.currentTab);
  const creationAdded = useSelector(state => state.uploadState?.uploadLoading);
  const uploadError = useSelector(state => state?.uploadState?.uploadError)
  const user = useSelector((state) => state.auth?.user || null);
  const session = useSelector((state) => state.auth?.session || null);
  const [activeTab, setActiveTab] = useState(_activeTab);
  const [uploadLoadingState, setUploadLoadingState] = useState(true);
  const [profileBannerDismissed, setProfileBannerDismissed] = useState(false);
  const [hasGlobalKycBanner, setHasGlobalKycBanner] = useState(() => {
    try {
      return document.body.classList.contains('has-global-kyc-banner');
    } catch {
      return false;
    }
  });
  const isEmailVerified = useMemo(() => {
    const confirmedAt = session?.user?.email_confirmed_at || session?.user?.confirmed_at;
    return !!confirmedAt;
  }, [session?.user?.email_confirmed_at, session?.user?.confirmed_at]);

  const passwordSetupPending = useMemo(
    () => requiresPasswordSetupFromSession(session),
    [session],
  );

  const hasCompletedProfileDetails = useMemo(() => {
    if (!user?.user_id) return false;
    const hasName = !!(`${user?.firstName || ''}`.trim()) && !!(`${user?.lastName || ''}`.trim());
    const hasPicture = !!(user?.profilePicture || '').trim();
    const hasBio = !!(user?.bio || '').trim();
    const isCreatorLike =
      user?.role === USER_ROLES.creator || user?.role === USER_ROLES.admin;
    const hasAnySocialLink = hasSocialLinks(user);
    return (
      hasName &&
      hasPicture &&
      hasBio &&
      (!isCreatorLike || hasAnySocialLink)
    );
  }, [user]);

  const shouldShowVerifyEmailBanner =
    userNeedsEmailVerificationOrPasswordSetup(user, session);
  const shouldShowCompleteProfileBanner = !!user?.user_id && isEmailVerified && !passwordSetupPending && !hasCompletedProfileDetails;

  const showCompleteProfileBanner =
    shouldShowCompleteProfileBanner &&
    !profileBannerDismissed &&
    !hasGlobalKycBanner;
  const completeProfileBannerClassName = `${styles.profileCompleteBanner} ${styles.profileCompleteBannerCustomer}`;
  const completeProfileBannerMessage = 'Complete your profile, help people discover you';
  const verifyEmailValue = searchParams.get('verifyEmail');
  const showVerifyEmailBanner = shouldShowVerifyEmailBanner;

  React.useEffect(() => {
    if (!user?.user_id) {
      setProfileBannerDismissed(false);
      return;
    }
    try {
      const storageKey = getProfileBannerDismissKey(user.user_id);
      setProfileBannerDismissed(localStorage.getItem(storageKey) === '1');
    } catch {
      setProfileBannerDismissed(false);
    }
  }, [user?.user_id]);

  React.useEffect(() => {
    let observer = null;
    try {
      observer = new MutationObserver(() => {
        setHasGlobalKycBanner(document.body.classList.contains('has-global-kyc-banner'));
      });
      observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
      setHasGlobalKycBanner(document.body.classList.contains('has-global-kyc-banner'));
    } catch {
      // Ignore observer setup issues in test/non-browser environments.
    }

    return () => {
      observer?.disconnect();
    };
  }, []);

  const handleDismissProfileBanner = useCallback(() => {
    setProfileBannerDismissed(true);
    try {
      if (!user?.user_id) return;
      const storageKey = getProfileBannerDismissKey(user.user_id);
      localStorage.setItem(storageKey, '1');
    } catch {
      // Ignore storage errors.
    }
  }, [user?.user_id]);

  const handleOpenCompleteProfile = useCallback(() => {
    navigate('/add-social-links');
  }, [navigate]);

  const handleOpenVerifyEmailModal = useCallback(() => {
    try {
      if (verifyEmailValue) {
        localStorage.setItem(VERIFY_EMAIL_BANNER_EMAIL_KEY, verifyEmailValue);
      }
    } catch {
      // ignore
    }
    sw?.openModal?.('verify-email');
  }, [sw, verifyEmailValue]);



  // Use scroll position hook for the current tab
  const { saveCurrentPosition } = useScrollPosition('explore', activeTab);

  // Memoize tab switching to prevent unnecessary re-renders
  const handleTabSwitch = useCallback((tab) => {

    // Save current scroll position before switching tabs
    saveCurrentPosition();
    dispatch(setCurrentTab(tab));
    setActiveTab(tab);
  }, [saveCurrentPosition]);

  const explorePageClassName = [
    styles.explorePage,
    (showCompleteProfileBanner || showVerifyEmailBanner) ? styles.explorePageWithBanner : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={explorePageClassName}>
      {creationAdded &&uploadLoadingState&&
        <ModalMessage
          children={<CreationAdded onClose={() => setUploadLoadingState(false)} />}
          onClose={() => setUploadLoadingState(false)} />
      }
      {showCompleteProfileBanner && (
        <div className={completeProfileBannerClassName}>
          <button
            type="button"
            className={styles.profileCompleteBannerText}
            onClick={handleOpenCompleteProfile}
          >
            {completeProfileBannerMessage}
          </button>
          <button
            type="button"
            className={styles.profileCompleteBannerClose}
            onClick={handleDismissProfileBanner}
            aria-label="Dismiss complete profile message"
          >
            ×
          </button>
        </div>
      )}
      {showVerifyEmailBanner && (
        <div className={styles.verifyEmailBanner}>
          <button
            type="button"
            className={styles.verifyEmailBannerText}
            onClick={handleOpenVerifyEmailModal}
          >
            Verify your email address ({verifyEmailValue || session?.user?.email || 'your email'}) and set your password to continue.
          </button>
        </div>
      )}
      <div className={`${styles.tabSwitcher} ${(showCompleteProfileBanner || showVerifyEmailBanner) ? styles.tabSwitcherWithBanner : ''} ${activeTab === 'heatmap' ? 'mb-0' : ''}`}>
        <button
          className={activeTab === 'feed' ? `${styles.tabButton} ${styles.tabButtonActive}` : styles.tabButton}
          onClick={() => handleTabSwitch('feed')}
        >
          CREATIONS
        </button>
        <button
          className={activeTab === 'heatmap' ? `${styles.tabButton} ${styles.tabButtonActive}` : styles.tabButton}
          onClick={() => handleTabSwitch('heatmap')}
        >
          HEATMAP (COMING SOON)
        </button>
      </div>


      {uploadError && <div className='error-upload'>

        <p className='fs-regular fw-bold fg-white ta-center mt-16'> An error occuried while loading your file</p>
        <div className='d-flex f-row aic jcc mt-12'>
          <span onClick={() => { dispatch(cleanError()) }} className='fs-xxs fw-bold fg-white td-underline d-flex jcc aic mr-8'>CANCEL</span>
          <button className='btn-retry d-flex jcc aic ml-8' onClick={() => { navigate("/creations/add") }}> TRY AGAIN</button>
        </div>

      </div>}


      <div>
        {activeTab === 'heatmap' && <ComingSoon />}
        <Suspense
          fallback={
            <div className="feed-tab">
              <div className="feed-tab__grid">
                {[1, 2, 3].map((item) => (
                  <CreationSkeleton key={item} />
                ))}
              </div>
            </div>
          }
        >
          {activeTab === 'feed' && <FeedTab />}
        </Suspense>
      </div>
    </div>
  )
}

export default Explore